import { TfNotification, transformNotification } from 'api';
import { NotificationsApi } from 'oas';
import { QueryClient, QueryKey, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { NOTIFICATION_REFRESH_INTERVAL } from 'shared';
import { useApiQuery } from '../use-api-query';
import { useAuth } from 'auth';
import React from 'react';

const NOTIFICATION_INVALIDATIONS: Record<string, QueryKey> = {
    ds_code_accepted: ['datasharing', 'account-links'],
    ds_share_accepted: ['datasharing', 'share-agreements'],
    ds_share_declined: ['datasharing', 'share-agreements'],
    ds_share_cancelled: ['datasharing', 'share-agreements'],
    ds_new_share: ['datasharing', 'share-agreements'],
};

export function invalidateDatasharingCache(type: string, queryClient?: QueryClient) {
    const key = NOTIFICATION_INVALIDATIONS[type];

    if (key) {
        queryClient?.invalidateQueries({ queryKey: key });
        return key;
    }

    return undefined;
}

export function isDatasharingNotification(notification: TfNotification) {
    return !!notification && notification.type.includes('ds_');
}

export const useGetNotifications = (
    ...parameters: Parameters<typeof NotificationsApi.prototype.getNotifications>
): UseQueryResult<TfNotification[]> => {
    const { user } = useAuth();
    const lastDSNotificationRef = React.useRef<string>('');
    const queryClient = useQueryClient();

    return useApiQuery(
        ['notifications', parameters],
        async (apiConfig, axiosInstance) => {
            const res = await new NotificationsApi(apiConfig, '', axiosInstance)
                .getNotifications(...parameters)
                .then((response) => ({
                    ...response,
                    data: response.data.map(transformNotification),
                }));

            const notifications = res.data;

            if (isDatasharingNotification(notifications[0]) && notifications[0].id !== lastDSNotificationRef.current) {
                invalidateDatasharingCache(notifications[0].type, queryClient);
                lastDSNotificationRef.current = notifications[0].id;
            }

            return res;
        },
        {
            refetchInterval: NOTIFICATION_REFRESH_INTERVAL,
            enabled:
                user &&
                !(user.need_to_set_password || user.need_to_accept_eula || user.need_to_setup_mfa || !user?.email),
        },
    );
};
